import React, { FunctionComponent, useState, useEffect } from 'react';
import { StepWizard, Step as WizzardStep } from '@inplayer-org/inplayer-ui';
import { useDispatch } from 'react-redux';
import snakeCase from 'lodash/snakeCase';

// actions
import { CreateItem, syncOvpVideos } from 'ducks/merchantDucks/items/actions';

// Components
import OvpVideoType from 'pages/MerchantPages/Assets/InitialCreate/OvpVideoType';
import AssetBasicInfo from 'pages/MerchantPages/Assets/InitialCreate/BasicInfo';
import QuickAddComponent from 'pages/MerchantPages/Assets/InitialCreate/QuickAdd';
import SyncVideos from 'pages/MerchantPages/Assets/InitialCreate/SyncVideos';
import AssetPricing from 'pages/MerchantPages/Assets/InitialCreate/Pricing';
import SyncJwPlayer from 'pages/MerchantPages/Assets/InitialCreate/SyncJwPlayer';
import AssetContentType, { EventType } from 'pages/MerchantPages/Assets/InitialCreate/ContentType';

// types
import { AccessControlType as AccessType } from 'ducks/merchantDucks/accesses/actions';
import { ItemDetails } from 'ducks/merchantDucks/items/types';
import { AssetType, Steps, OvpCategoryType } from '../assetTypes';

export interface Step extends WizzardStep {
  id: Steps;
}

interface StepProgress {
  id: number;
  isDisabled: boolean;
  isCompleted: boolean;
}

const initialStepCompletion: Array<StepProgress> = [
  { id: 0, isDisabled: false, isCompleted: false },
  { id: 1, isDisabled: true, isCompleted: false },
  { id: 2, isDisabled: true, isCompleted: false },
  { id: 3, isDisabled: true, isCompleted: false },
  { id: 4, isDisabled: true, isCompleted: false },
  { id: 5, isDisabled: true, isCompleted: false },
];

enum AccessControlType {
  paid = AccessType.PAID,
  code = AccessType.CODEONLY,
  capture = AccessType.DATA,
  codeauth = AccessType.CODEAUTH,
}
interface OwnProps {
  activeStep: number;
  onSubmit: (asset: CreateItem) => any;
  onStepChange: (step: number) => void;
  closeModal: () => void;
  filterSteps?: (...args: any) => boolean;
  isTemplate?: boolean;
  item?: ItemDetails;
}

type Props = OwnProps;

const AssetCreateWizard: FunctionComponent<Props> = ({
  onSubmit,
  activeStep,
  onStepChange,
  closeModal,
  filterSteps = () => true,
  isTemplate = false,
  item,
}) => {
  const [assetTitle, setAssetTitle] = useState(item?.title || '');
  const [assetType, setAssetType] = useState(item?.item_type.name || '');
  const [ovpType, setOvpType] = useState(item?.item_type.host || '');
  const [accessControlType, setAccessControlType] = useState(item?.access_control_type?.id || 0);
  const [assetContentType, setAssetContentType] = useState(item?.event_type || '');

  const [ovpCategory, setOvpCategory] = useState('');
  const [steps, setSteps] = useState(initialStepCompletion);

  const [displayErrorMessage, checkAssetTitleLength] = useState(false);
  const [selectedVideos, setSelectedVideos] = useState({});
  const [isAllSynced, setIsAllSynced] = useState(false);

  const namePlaceholder = isTemplate ? 'Template' : 'Asset';
  const firstStepTitle = `Select ${namePlaceholder.toLowerCase()}) type`;
  const isQuickAdd = ovpCategory === OvpCategoryType.QuickAdd;

  const onAssetTypeSelect = (type: string) => {
    setAssetType(type);
    setOvpType('');
  };

  const dispatch = useDispatch();

  const toggleStepCompletion = () => {
    const previousStepId = activeStep - 1;

    setSteps(() =>
      steps.map((step) => {
        if (step.id === activeStep) {
          return { ...step, isDisabled: false };
        }
        if (step.id === previousStepId) {
          return { ...step, isCompleted: true };
        }
        return step;
      })
    );
  };

  useEffect(() => {
    toggleStepCompletion();
  }, [activeStep]);

  const handleCreateAsset = async (title?: string) => {
    // preview details for the asset must be added in metadata
    const metadata = {
      preview_description: '<p>Asset description</p>',
      preview_title: '<h3>Asset title</h3>',
      paywall_cover_photo: 'https://inplayer-paywall-v2.s3.amazonaws.com/images/ip-preview-premium.jpg',
      preview_button_label: 'Buy',
    };

    const asset: CreateItem = {
      title: title || assetTitle,
      itemType: ovpType ? `${ovpType.replace(/jwplayer/, 'jw')}_asset` : assetType,
      metadata,
      accessControlTypeId: accessControlType,
      assetContentType,
    };

    try {
      await onSubmit(asset);
    } catch (e) {
      console.error(e);
    } finally {
      closeModal();
    }
  };

  // first, initial step callback
  const onSaveBasicAssetInfo = async (assetTitleParam: string, isLastStep: boolean) => {
    const assetTitleLength = assetTitleParam.length;

    if (assetTitleLength > 2 && assetTitleLength <= 191) {
      setAssetTitle(assetTitleParam);

      if (isLastStep) {
        handleCreateAsset(assetTitleParam);
      } else {
        onStepChange(activeStep + 1);
      }

      // remove all selections when moving to next screen
      document.getSelection()?.removeAllRanges();
    }

    checkAssetTitleLength(assetTitleLength <= 2 || assetTitleLength > 191);
  };

  const onOvpSelected = (ovpTypeParam: string) => setOvpType(ovpTypeParam);

  // second step callback
  const onSubmitAssetContentType = (contentType: string) => {
    setAssetContentType(contentType);
    onStepChange(activeStep + 1);
  };

  // third step callback
  const onSubmitAssetControlType = (isLastStep: boolean) => {
    if (isLastStep) {
      handleCreateAsset();
    } else {
      onStepChange(activeStep + 1);
    }
  };

  // fourth step callback
  const onCompleteOvpSelection = (ovpCategoryParam: string) => () => {
    if (ovpCategoryParam !== OvpCategoryType.AddOneVideo) {
      setOvpCategory(ovpCategoryParam);
      onStepChange(activeStep + 1);
    } else {
      handleCreateAsset();
    }
  };

  const syncVideos = (checkedVideos: Record<string, boolean>, syncAll: boolean): void => {
    const videosIds: Array<string> = Object.keys(checkedVideos).filter((key) => checkedVideos[key]);
    dispatch(syncOvpVideos({ ovpName: ovpType, videosIds, syncAll, isUpdate: false, accessControlType }));
    closeModal();
  };

  // fourth step callback if jw asset is selected
  const onCompleteVideosSelection = (checkedVideos: Record<string, boolean>, syncAll: boolean) => () => {
    if (ovpType === 'jwplayer' && ovpCategory === OvpCategoryType.SyncVideos) {
      setSelectedVideos(checkedVideos);
      setIsAllSynced(syncAll);
      onStepChange(activeStep + 1);
    } else {
      syncVideos(checkedVideos, syncAll);
    }
  };

  const onBackButtonClick = () => {
    onStepChange(activeStep - 1);
  };

  const generateStepItems = () => {
    const isJwPlayerSync = ovpType === 'jwplayer' && ovpCategory === OvpCategoryType.SyncVideos;
    const stepItems: Array<Step> = [
      {
        id: Steps.BasicAssetInfo,
        isDisabled: steps[0].isDisabled,
        isCompleted: steps[0].isCompleted,
        component: (
          <AssetBasicInfo
            assetTitle={assetTitle}
            assetType={assetType}
            onSaveBasicAssetInfo={(title: string) => {
              onSaveBasicAssetInfo(title, stepItems.length === 1);
            }}
            onAssetTypeSelect={onAssetTypeSelect}
            displayErrorMessage={displayErrorMessage}
            presetAssetTitle={item?.title || ''}
            isTemplate={isTemplate}
            stepTitle={firstStepTitle}
          />
        ),
        tag: snakeCase(firstStepTitle),
      },

      {
        id: Steps.AssetContentType,
        isDisabled: steps[1].isDisabled,
        isCompleted: steps[1].isCompleted,
        component: (
          <AssetContentType
            assetContentType={assetContentType}
            setAssetContentType={(val: string) => setAssetContentType(val)}
            onSubmitAssetContentType={(contentType: string) => {
              onSubmitAssetContentType(contentType);
            }}
            onBackButtonClick={onBackButtonClick}
          />
        ),
        tag: 'select_content_category',
      },
      {
        id: Steps.Pricing,
        isDisabled: steps[2].isDisabled,
        isCompleted: steps[2].isCompleted,
        component: (
          <AssetPricing
            accessControlType={AccessControlType[accessControlType]}
            setAccessControlType={(val: string) => setAccessControlType(AccessControlType[val])}
            onSubmitAssetControlType={() => {
              onSubmitAssetControlType(stepItems.length === 3);
            }}
            onBackButtonClick={onBackButtonClick}
          />
        ),
        tag: 'select_access_type',
      },
      {
        id: Steps.OvpVideos,
        isDisabled: steps[3].isDisabled,
        isCompleted: steps[3].isCompleted,
        component: (
          <OvpVideoType
            onOvpSelected={onOvpSelected}
            onCompleteSelection={onCompleteOvpSelection}
            onBackButtonClick={onBackButtonClick}
            eventType={assetContentType as EventType}
          />
        ),
        tag: 'select_your_online_video_platform',
      },
      {
        id: Steps.OvpVideoQuickAdd,
        isDisabled: steps[4].isDisabled,
        isCompleted: steps[4].isCompleted,
        component: isQuickAdd ? (
          <QuickAddComponent
            accessControlTypeId={accessControlType}
            ovpType={ovpType}
            onBackButtonClick={onBackButtonClick}
          />
        ) : (
          <SyncVideos
            ovpType={ovpType}
            accessControlType={accessControlType}
            closeModal={closeModal}
            isJwPlayerSync={isJwPlayerSync}
            onCompleteSelection={onCompleteVideosSelection}
            onBackButtonClick={onBackButtonClick}
          />
        ),
        tag: isQuickAdd ? `${ovpType}_details` : 'sync_all_videos',
      },
    ]
      .filter((step: Step) => {
        const { id } = step;

        if (assetType === AssetType.Html) {
          return [Steps.BasicAssetInfo, Steps.AssetContentType, Steps.Pricing].includes(id);
        }
        if (assetType === AssetType.JWOTT) {
          return [Steps.BasicAssetInfo].includes(id);
        }

        return true;
      })
      .filter(filterSteps);

    if (isJwPlayerSync) {
      const selectedVideosIds: Array<string> = Object.keys(selectedVideos).filter((key) => selectedVideos[key]);
      stepItems.push({
        id: Steps.SyncJwPlayer,
        isDisabled: steps[5].isDisabled,
        isCompleted: steps[5].isCompleted,
        component: (
          <SyncJwPlayer
            videosIds={selectedVideosIds}
            syncAll={isAllSynced}
            ovpName={ovpType}
            accessControlType={accessControlType}
            closeModal={closeModal}
            onBackButtonClick={onBackButtonClick}
          />
        ),
        tag: 'select_video_player',
      });
    }

    return stepItems;
  };

  return <StepWizard steps={generateStepItems()} activeStep={activeStep} onStepChange={onStepChange} />;
};

export default AssetCreateWizard;
